import { useCallback, useEffect, useRef, useState } from "react";

import {
  isContentItem,
  itemEditText,
  LimitedListItemEdit,
  ListItem,
  parseFunctionCallEdit,
} from "./limitedListItems";

// At most one row edits at a time; `index` is the item's position in the
// unsorted data, stable across the ordering toggle.
export type ItemEditing = { index: number; text: string; isInvalid?: boolean };

// The row editor's lifecycle: open on one row, track its text, apply on a
// click outside or a Tab, close.
const useItemEditing = (
  data: Array<ListItem>,
  editItem: (index: number, edit: LimitedListItemEdit) => void
) => {
  const [editing, setEditing] = useState<ItemEditing | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Applies the open edit and closes the editor. On a function-call row
  // garbage can't be applied, so the editor stays open, flagged, until the
  // JSON parses or the text goes back.
  const applyEditing = useCallback(() => {
    if (!editing) {
      return;
    }
    const item = data[editing.index];
    if (!item || editing.text === itemEditText(item)) {
      setEditing(null);
      return;
    }
    if (isContentItem(item)) {
      setEditing(null);
      editItem(editing.index, { content: editing.text });
      return;
    }
    const functionCall = parseFunctionCallEdit(editing.text);
    if (!functionCall) {
      setEditing({ ...editing, isInvalid: true });
      return;
    }
    setEditing(null);
    editItem(editing.index, { function_call: functionCall });
  }, [editing, data, editItem]);

  useEffect(() => {
    if (!editing) {
      return;
    }
    // Clicking anywhere outside the input applies the edit. On mousedown so
    // a click landing on another row applies this one before opening that
    // one.
    const handleMouseDown = (event: MouseEvent) => {
      if (inputRef.current?.contains(event.target as Node)) {
        return;
      }
      applyEditing();
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [editing, applyEditing]);

  return {
    editing,
    inputRef,
    startEditing: (index: number, text: string) => setEditing({ index, text }),
    // A change clears the invalid flag until the next apply.
    changeEditing: (text: string) =>
      setEditing((current) => current && { index: current.index, text }),
    cancelEditing: () => setEditing(null),
    applyEditing,
  };
};

export default useItemEditing;
